import { useState, useEffect } from 'react';
import { Bell, BellOff, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import { isPushSubscribed, subscribeToPush, unsubscribeFromPush } from '../utils/pwa';
import { useAuth } from '../contexts/AuthContext';

export default function PushNotificationPrompt() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [busy, setBusy] = useState(false);

  // Push prompt: show if supported, not yet subscribed, not denied, and user hasn't dismissed in last 7 days
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      if (Notification.permission !== 'default') return;
      const dismissed = localStorage.getItem('jsf_push_dismissed');
      if (dismissed) {
        const ts = parseInt(dismissed, 10);
        if (Date.now() - ts < 7 * 24 * 3600 * 1000) return;
      }
      const subscribed = await isPushSubscribed().catch(() => false);
      if (cancelled || subscribed) return;
      // Delay 6s so it doesn't hijack initial page load
      const t = setTimeout(() => setShow(true), 6000);
      return () => clearTimeout(t);
    })();
    return () => { cancelled = true; };
  }, []);

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      const dismissed = localStorage.getItem('jsf_install_dismissed');
      if (dismissed && Date.now() - parseInt(dismissed, 10) < 14 * 24 * 3600 * 1000) return;
      setTimeout(() => setShowInstall(true), 10000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleSubscribe = async () => {
    setBusy(true);
    try {
      await subscribeToPush(user?.email || null);
      toast.success('Notifications enabled');
      setShow(false);
    } catch (e) {
      toast.error(e.message || 'Failed to enable notifications');
    }
    setBusy(false);
  };

  const dismissPush = () => {
    localStorage.setItem('jsf_push_dismissed', String(Date.now()));
    setShow(false);
  };

  const handleInstall = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === 'accepted') toast.success('JS Fragrance installed');
    setInstallEvent(null);
    setShowInstall(false);
  };

  const dismissInstall = () => {
    localStorage.setItem('jsf_install_dismissed', String(Date.now()));
    setShowInstall(false);
  };

  return (
    <>
      {show && (
        <div
          data-testid="push-prompt"
          className="fixed top-20 right-4 sm:top-24 sm:right-6 z-[90] w-80 max-w-[90vw] animate-fade-in"
        >
          <div className="bg-[#0A0A0A]/95 backdrop-blur-md border border-[#1C7D3F]/40 p-4 shadow-2xl">
            <div className="flex items-start gap-3 mb-3">
              <Bell className="w-5 h-5 text-[#1C7D3F] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-white text-sm font-sans font-medium mb-0.5">Stay in the loop</p>
                <p className="text-white/50 text-xs font-sans leading-relaxed">
                  Get notified about new drops, restocks, and order updates.
                </p>
              </div>
              <button onClick={dismissPush} className="text-white/30 hover:text-white" aria-label="Dismiss">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={dismissPush}
                data-testid="push-dismiss"
                className="flex-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-sans border border-white/15 text-white/60 hover:border-white/30 hover:text-white transition-colors"
              >
                Not Now
              </button>
              <button
                onClick={handleSubscribe}
                disabled={busy}
                data-testid="push-enable"
                className="flex-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-sans bg-[#1C7D3F] text-white hover:bg-[#2E9B50] transition-colors font-medium disabled:opacity-50"
              >
                {busy ? '...' : 'Enable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInstall && (
        <div
          data-testid="install-prompt"
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[90] w-80 max-w-[90vw] animate-fade-in"
        >
          <div className="bg-[#0A0A0A]/95 backdrop-blur-md border border-[#D4AF37]/40 p-4 shadow-2xl">
            <div className="flex items-start gap-3 mb-3">
              <Download className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-white text-sm font-sans font-medium mb-0.5">Install JS Fragrance</p>
                <p className="text-white/50 text-xs font-sans leading-relaxed">
                  Add to your home screen for instant access to drops &amp; orders.
                </p>
              </div>
              <button onClick={dismissInstall} className="text-white/30 hover:text-white" aria-label="Dismiss">
                <X className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleInstall}
              data-testid="install-confirm"
              className="w-full px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-sans bg-[#D4AF37] text-black hover:bg-[#E5C75A] transition-colors font-medium"
            >
              Install App
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** Settings-style toggle for /account page so users can manage their subscription. */
export function PushSubscriptionToggle({ userEmail }) {
  const [enabled, setEnabled] = useState(false);
  const [supported, setSupported] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      return;
    }
    isPushSubscribed().then(setEnabled).catch(() => {});
  }, []);

  if (!supported) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) {
        await unsubscribeFromPush();
        toast.success('Notifications disabled');
        setEnabled(false);
      } else {
        await subscribeToPush(userEmail || null);
        toast.success('Notifications enabled');
        setEnabled(true);
      }
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
    setBusy(false);
  };

  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid="account-push-toggle"
      className="flex items-center justify-between w-full p-3 bg-[#141414] border border-white/10 hover:border-[#1C7D3F]/40 transition-colors text-left"
    >
      <div className="flex items-center gap-3">
        {enabled ? <Bell className="w-4 h-4 text-[#1C7D3F]" /> : <BellOff className="w-4 h-4 text-white/40" />}
        <div>
          <p className="text-white text-sm font-sans">Push Notifications</p>
          <p className="text-white/40 text-xs font-sans">{enabled ? 'On — order updates &amp; drops' : 'Off — turn on for alerts'}</p>
        </div>
      </div>
      <span className={`text-[10px] uppercase tracking-[0.2em] font-sans ${enabled ? 'text-[#1C7D3F]' : 'text-white/30'}`}>
        {enabled ? 'Enabled' : 'Disabled'}
      </span>
    </button>
  );
}
